import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  formatHours,
  type LineClassificationDto,
  type MeResponse,
  type ProjectDto,
  type ProjectTeamSummaryDto,
  type TaskDto,
} from '@time-tracking/shared';

import {
  useAddAssignment,
  useAddTeamToTask,
  useCreateTask,
  useDeleteTask,
  usePatchTask,
  useRemoveAssignment,
  useRemoveTeamFromTask,
  useTasks,
} from '../../../api/projects.js';
import { AddAssignmentDialog } from '../dialogs/AddAssignmentDialog.js';
import { DeleteTaskDialog } from '../dialogs/DeleteTaskDialog.js';
import { EditTaskDialog } from '../dialogs/EditTaskDialog.js';

type TeamOption = MeResponse['teams'][number];

// Width of the per-task team picker popover (px) — shared by the positioning
// clamp and the popover style so they can't drift apart.
const PICKER_WIDTH = 240;

interface TasksPaneProps {
  project: ProjectDto;
  classifications: LineClassificationDto[];
  teams: TeamOption[];
  onToast: (msg: string, kind: 'success' | 'error') => void;
}

/**
 * The classification a new task starts on: the admin-flagged default, falling
 * back to the first active row.
 *
 * The fallback is not dead code. Zero defaults is a legal, representable state —
 * migration 0028 runs before the seed, so a fresh database briefly has none — and
 * the old behaviour was exactly this fallback. What it must never be again is the
 * *primary* rule: list position is not a product decision, and reading it as one
 * is what let PR #77's rename silently change every new task's type.
 *
 * Callers pass the already-active-filtered list; passing the full list would
 * happily return a deactivated default.
 */
function defaultClassificationId(activeClassifications: LineClassificationDto[]): string {
  const flagged = activeClassifications.find((c) => c.isDefault);
  return flagged?.classificationId ?? activeClassifications[0]?.classificationId ?? '';
}

function ClassificationTag({
  classificationId,
  classifications,
}: {
  classificationId: string;
  classifications: LineClassificationDto[];
}) {
  const cls = classifications.find((c) => c.classificationId === classificationId);
  return <span className="cls-tag">{cls?.name ?? '—'}</span>;
}

// On-brand avatar tints only (green / bluebonnet / gold / charcoal-on-grey /
// charcoal-on-warm). No red or purple — off-brand per Brand Book V2.1.
const AVATAR_COLORS = [
  { bg: '#e8f3e0', fg: '#2a6017' },
  { bg: '#e3eef5', fg: '#005f86' },
  { bg: '#fff6e0', fg: '#7a5a00' },
  { bg: '#e6ebee', fg: '#333f48' },
  { bg: '#ffe8e3', fg: '#333f48' },
];

function avatarColor(userId: string) {
  const sum = [...userId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length] ?? { bg: '#e6ebee', fg: '#333f48' };
}

function AssignedTo({ task, teamMap }: { task: TaskDto; teamMap: Map<string, string> }) {
  const teamIds = task.teamIds ?? [];
  const assignees = task.assignees ?? [];

  if (teamIds.length === 0 && assignees.length === 0) {
    return <span style={{ color: 'var(--fg3)' }}>—</span>;
  }

  const visible = assignees.slice(0, 3);
  const overflow = assignees.length - 3;

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {teamIds.map((id) => (
        <span key={id} className="assign-chip assign-chip--team" title={teamMap.get(id) ?? id}>
          <svg
            width="11"
            height="11"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M1 11c0-2 1.8-3.5 4-3.5h4c2.2 0 4 1.5 4 3.5" />
            <circle cx="5" cy="4.5" r="2" />
            <circle cx="9" cy="4.5" r="2" />
          </svg>
          {teamMap.get(id) ?? id.slice(0, 8) + '…'} · team
        </span>
      ))}
      {visible.map((a) => {
        const { bg, fg } = avatarColor(a.userId);
        return (
          <span
            key={a.userId}
            className="assign-chip assign-chip--indiv"
            title={a.displayName}
            style={{ background: bg, color: fg, borderColor: fg + '55' }}
          >
            <span className="sr-only">{a.displayName}</span>
            <span aria-hidden="true">{a.initials}</span>
          </span>
        );
      })}
      {overflow > 0 && (
        <span className="assign-chip" title={`${overflow} more`}>
          +{overflow}
        </span>
      )}
    </div>
  );
}

function TeamIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 11c0-2 1.8-3.5 4-3.5h4c2.2 0 4 1.5 4 3.5" />
      <circle cx="5" cy="4.5" r="2" />
      <circle cx="9" cy="4.5" r="2" />
    </svg>
  );
}

// Why every staffing control below is pre-disabled on an archived project: the
// server 409s on POST /projects/:id/tasks, POST /tasks/:id/assignments and POST
// /tasks/:id/team-memberships once `archived_at` is set. Nothing on this screen
// would tell the manager that in advance, so the click would spend a round trip
// to produce an error they could not have predicted. Same shape as the existing
// `task.lockedAt` disables, tooltip and all.
const ARCHIVED_HINT = 'This project is archived — restore it to add tasks or staffing';

export function TasksPane({ project, classifications, teams, onToast }: TasksPaneProps) {
  const { data: tasks, isLoading, error } = useTasks(project.projectId);
  const isArchived = project.archivedAt !== null;
  const createMutation = useCreateTask();
  const patchMutation = usePatchTask();
  const deleteMutation = useDeleteTask();
  const addTeamMutation = useAddTeamToTask();
  const removeTeamMutation = useRemoveTeamFromTask();
  const addAssignmentMutation = useAddAssignment();
  const removeAssignmentMutation = useRemoveAssignment();

  const [editingTask, setEditingTask] = useState<TaskDto | null>(null);
  const [deletingTask, setDeletingTask] = useState<TaskDto | null>(null);
  const [addUserTask, setAddUserTask] = useState<TaskDto | null>(null);
  const [teamPopoverTaskId, setTeamPopoverTaskId] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<{ top: number; left: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  function dismissTeamPicker() {
    setTeamPopoverTaskId(null);
    setPopoverAnchor(null);
  }

  // Close the picker on outside-click or Escape; Escape restores focus to the trigger.
  useEffect(() => {
    if (!teamPopoverTaskId) return;
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      dismissTeamPicker();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        dismissTeamPicker();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [teamPopoverTaskId]);

  // Move focus into the picker when it opens — the first team toggle, or the
  // dialog container itself when the palette is empty (no button to land on),
  // so focus is inside the dialog and Escape / focus-out dismissal still work.
  useEffect(() => {
    if (!teamPopoverTaskId) return;
    const el = popoverRef.current;
    (el?.querySelector('button') ?? el)?.focus();
  }, [teamPopoverTaskId]);

  // If the open picker's task disappears (deleted, or refetched away), close the
  // picker so its state and document listeners don't linger with no visible UI.
  useEffect(() => {
    if (teamPopoverTaskId && !(tasks ?? []).some((t) => t.taskId === teamPopoverTaskId)) {
      dismissTeamPicker();
    }
  }, [tasks, teamPopoverTaskId]);

  function toggleTeamPicker(taskId: string, trigger: HTMLButtonElement) {
    if (teamPopoverTaskId === taskId) {
      dismissTeamPicker();
      return;
    }
    const rect = trigger.getBoundingClientRect();
    triggerRef.current = trigger;
    setTeamPopoverTaskId(taskId);
    // Anchor the 240px picker to the trigger's right edge, but keep it fully on
    // screen: clamp between an 8px left margin and the right viewport edge.
    const left = Math.max(
      8,
      Math.min(rect.right - PICKER_WIDTH, window.innerWidth - PICKER_WIDTH - 8),
    );
    setPopoverAnchor({ top: rect.bottom + 6, left });
  }

  const [showAddRow, setShowAddRow] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  // New tasks may only be assigned an active classification; deactivated ones
  // are kept for history but not offered for new selection.
  const activeClassifications = classifications.filter((c) => c.activeFlag);
  const [newTaskClsId, setNewTaskClsId] = useState(defaultClassificationId(activeClassifications));
  // Keep the add-row's selected type valid if classifications refresh (e.g. the
  // selected type is deactivated elsewhere) so the <select> never holds a value
  // absent from its options (blank UI + a 409 on submit). A still-valid choice is
  // deliberately kept: falling back to the default on every refetch would stomp a
  // selection the user made mid-edit.
  useEffect(() => {
    const active = classifications.filter((c) => c.activeFlag);
    setNewTaskClsId((cur) =>
      cur && active.some((c) => c.classificationId === cur) ? cur : defaultClassificationId(active),
    );
  }, [classifications]);

  // Resolve chip labels from both the user's own teams and the project palette
  // (a lead may assign a palette team they don't personally belong to).
  const teamMap = new Map<string, string>(teams.map((t) => [t.teamId, t.name]));
  for (const t of project.teams) teamMap.set(t.teamId, t.name);

  function handleAddTask(e: React.FormEvent) {
    e.preventDefault();
    if (!newTaskName.trim() || !newTaskClsId) return;
    createMutation.mutate(
      {
        projectId: project.projectId,
        data: { name: newTaskName.trim(), classificationId: newTaskClsId },
      },
      {
        onSuccess: () => {
          setNewTaskName('');
          setShowAddRow(false);
          onToast('Task added', 'success');
        },
        onError: (err) => onToast(err.message, 'error'),
      },
    );
  }

  function handleEdit(updates: { name: string; classificationId: string; activeFlag: boolean }) {
    if (!editingTask) return;
    patchMutation.mutate(
      { taskId: editingTask.taskId, projectId: project.projectId, patch: updates },
      {
        onSuccess: () => {
          setEditingTask(null);
          onToast('Task updated', 'success');
        },
        onError: (err) => onToast(err.message, 'error'),
      },
    );
  }

  function handleDeleteConfirm() {
    if (!deletingTask) return;
    deleteMutation.mutate(
      { taskId: deletingTask.taskId, projectId: project.projectId },
      {
        onSuccess: () => {
          setDeletingTask(null);
          onToast(`"${deletingTask.name}" archived`, 'success');
        },
        onError: (err) => onToast(err.message, 'error'),
      },
    );
  }

  // Model B: teams are assigned to individual tasks from the project palette
  // (project.teams). Adding a team to a task never fans out; removing it from
  // one task leaves the others untouched.
  function handleTaskTeamToggle(task: TaskDto, team: ProjectTeamSummaryDto, isAssigned: boolean) {
    if (isAssigned) {
      removeTeamMutation.mutate(
        { taskId: task.taskId, projectId: project.projectId, teamId: team.teamId },
        {
          onSuccess: () => onToast(`Removed ${team.name} from "${task.name}"`, 'success'),
          onError: (err) => onToast(err.message, 'error'),
        },
      );
    } else {
      addTeamMutation.mutate(
        { taskId: task.taskId, projectId: project.projectId, teamId: team.teamId },
        {
          onSuccess: () => onToast(`Added ${team.name} to "${task.name}"`, 'success'),
          onError: (err) => onToast(err.message, 'error'),
        },
      );
    }
  }

  async function handleAssignmentSubmit(toAdd: string[], toRemoveAssignmentIds: string[]) {
    if (!addUserTask) return;
    try {
      await Promise.all([
        ...toAdd.map((userId) =>
          addAssignmentMutation.mutateAsync({
            taskId: addUserTask.taskId,
            projectId: project.projectId,
            data: { userId },
          }),
        ),
        ...toRemoveAssignmentIds.map((assignmentId) =>
          removeAssignmentMutation.mutateAsync({
            taskId: addUserTask.taskId,
            assignmentId,
            projectId: project.projectId,
          }),
        ),
      ]);
      const parts: string[] = [];
      if (toAdd.length > 0) parts.push(`${toAdd.length} added`);
      if (toRemoveAssignmentIds.length > 0) parts.push(`${toRemoveAssignmentIds.length} removed`);
      onToast(parts.join(', '), 'success');
      setAddUserTask(null);
    } catch (err) {
      onToast((err as Error).message, 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="pane-loading" aria-live="polite">
        Loading tasks…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pane-error" role="alert">
        Failed to load tasks: {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="tasks-toolbar">
        <span style={{ fontSize: 'var(--fs-14)', color: 'var(--fg3)' }}>
          {tasks?.length ?? 0} task{tasks?.length !== 1 ? 's' : ''}
        </span>
        {!showAddRow && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setShowAddRow(true);
              setNewTaskClsId(defaultClassificationId(activeClassifications));
            }}
            disabled={isArchived}
            title={isArchived ? ARCHIVED_HINT : undefined}
          >
            + Add task
          </button>
        )}
      </div>

      {/* A browser does not show a `title` tooltip on a DISABLED control, so the
          reason has to be readable on the page itself — not only on the buttons.
          Colour carries nothing here: it is a sentence. */}
      {isArchived && (
        <p className="pane-archived-note">
          <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
            <path d="M1 2.5A.5.5 0 0 1 1.5 2h9a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-1zM2 5h8v4.5a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5V5zm2.5 1.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z" />
          </svg>
          <span>
            This project is archived, so no new tasks or staffing can be added. Existing tasks and
            hours are unchanged, and can still be edited. Choose <strong>Restore…</strong> above to
            put it back.
          </span>
        </p>
      )}

      <div className="inbox-table-wrap">
        <table className="tasks-table" aria-label="Project tasks">
          <thead>
            <tr>
              <th scope="col">Task</th>
              <th scope="col">Type</th>
              <th scope="col">Assigned to</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Hours
              </th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks?.map((task) => (
              <tr key={task.taskId} className={task.activeFlag ? '' : 'task-row__inactive'}>
                <td style={{ fontWeight: 'var(--fw-medium)' }}>
                  {task.name}
                  {!task.activeFlag && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        color: 'var(--fg3)',
                        fontWeight: 'var(--fw-regular)',
                      }}
                    >
                      (inactive)
                    </span>
                  )}
                </td>
                <td>
                  <ClassificationTag
                    classificationId={task.classificationId}
                    classifications={classifications}
                  />
                </td>
                <td>
                  <AssignedTo task={task} teamMap={teamMap} />
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 'var(--fs-14)',
                  }}
                >
                  {task.totalMinutes ? `${formatHours(task.totalMinutes)}h` : '—'}
                </td>
                <td>
                  <div className="task-actions">
                    {(() => {
                      // One palette team → the button is a direct on/off toggle
                      // (no picker needed). Zero or 2+ → open the picker popover.
                      const only = project.teams.length === 1 ? project.teams[0] : undefined;
                      if (only) {
                        const isAssigned = (task.teamIds ?? []).includes(only.teamId);
                        return (
                          <button
                            type="button"
                            className={`icon-btn${isAssigned ? ' icon-btn--active' : ''}`}
                            onClick={() => handleTaskTeamToggle(task, only, isAssigned)}
                            disabled={!!task.lockedAt || isArchived}
                            aria-pressed={isAssigned}
                            aria-label={
                              isAssigned
                                ? `Remove ${only.name} from task ${task.name}`
                                : `Add ${only.name} to task ${task.name}`
                            }
                            title={
                              isArchived
                                ? ARCHIVED_HINT
                                : task.lockedAt
                                  ? 'Locked tasks cannot be changed'
                                  : isAssigned
                                    ? `Remove ${only.name}`
                                    : `Add ${only.name}`
                            }
                          >
                            <TeamIcon />
                          </button>
                        );
                      }
                      const assignedCount = task.teamIds?.length ?? 0;
                      return (
                        <button
                          type="button"
                          className={`icon-btn${assignedCount > 0 ? ' icon-btn--active' : ''}`}
                          onClick={(e) => toggleTeamPicker(task.taskId, e.currentTarget)}
                          disabled={!!task.lockedAt || isArchived}
                          aria-haspopup="dialog"
                          aria-expanded={teamPopoverTaskId === task.taskId}
                          aria-label={
                            assignedCount > 0
                              ? `Assign teams to task ${task.name} (${assignedCount} assigned)`
                              : `Assign teams to task ${task.name}`
                          }
                          title={
                            isArchived
                              ? ARCHIVED_HINT
                              : task.lockedAt
                                ? 'Locked tasks cannot be changed'
                                : 'Assign teams to this task'
                          }
                        >
                          <TeamIcon />
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setAddUserTask(task)}
                      disabled={!!task.lockedAt || isArchived}
                      aria-label={`Add person to task ${task.name}`}
                      title={
                        isArchived
                          ? ARCHIVED_HINT
                          : task.lockedAt
                            ? 'Locked tasks cannot be changed'
                            : 'Add person to this task'
                      }
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M9 11v-1a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v1" />
                        <circle cx="5.5" cy="4" r="2" />
                        <path d="M12 4v4M10 6h4" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => setEditingTask(task)}
                      aria-label={`Edit task ${task.name}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="icon-btn icon-btn--destructive"
                      onClick={() => setDeletingTask(task)}
                      aria-label={`Archive task ${task.name}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4.5 3.5l.5 8h5l.5-8" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {showAddRow && (
              <tr>
                <td colSpan={5}>
                  <form
                    onSubmit={handleAddTask}
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                      padding: '4px 0',
                      flexWrap: 'wrap',
                    }}
                    aria-label="Add new task"
                  >
                    <input
                      type="text"
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      placeholder="Task name…"
                      aria-label="New task name"
                      aria-required="true"
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        minHeight: 36,
                        border: '1px solid var(--ut-charcoal)',
                        borderRadius: 4,
                        fontFamily: 'inherit',
                        fontSize: 'var(--fs-14)',
                        minWidth: 160,
                      }}
                    />
                    <select
                      value={newTaskClsId}
                      onChange={(e) => setNewTaskClsId(e.target.value)}
                      aria-label="Task type"
                      style={{
                        padding: '6px 10px',
                        minHeight: 36,
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        fontFamily: 'inherit',
                        fontSize: 'var(--fs-14)',
                      }}
                    >
                      {activeClassifications.map((c) => (
                        <option key={c.classificationId} value={c.classificationId}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={!newTaskName.trim() || !newTaskClsId || createMutation.isPending}
                      aria-busy={createMutation.isPending}
                    >
                      {createMutation.isPending ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => {
                        setShowAddRow(false);
                        setNewTaskName('');
                      }}
                    >
                      Cancel
                    </button>
                  </form>
                </td>
              </tr>
            )}

            {!tasks?.length && !showAddRow && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    textAlign: 'center',
                    padding: 'var(--space-6)',
                    color: 'var(--fg3)',
                    fontStyle: 'italic',
                  }}
                >
                  No tasks yet. Add the first task above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddAssignmentDialog
        task={addUserTask}
        project={project}
        teams={teams}
        totalTaskCount={tasks?.length ?? 0}
        onClose={() => setAddUserTask(null)}
        onSubmit={handleAssignmentSubmit}
        isPending={addAssignmentMutation.isPending || removeAssignmentMutation.isPending}
      />

      <EditTaskDialog
        task={editingTask}
        classifications={classifications}
        onClose={() => setEditingTask(null)}
        onSubmit={handleEdit}
        isPending={patchMutation.isPending}
      />

      <DeleteTaskDialog
        task={deletingTask}
        onClose={() => setDeletingTask(null)}
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />

      {teamPopoverTaskId &&
        popoverAnchor &&
        (() => {
          const task = tasks?.find((t) => t.taskId === teamPopoverTaskId);
          if (!task) return null;
          const assigned = new Set(task.teamIds ?? []);
          return createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={`Assign teams to ${task.name}`}
              tabIndex={-1}
              onBlur={(e) => {
                // Tabbing (or otherwise moving focus) out of the popover closes it,
                // so it never lingers open while disconnected from keyboard focus.
                const next = e.relatedTarget as Node | null;
                if (!next) return; // non-focusable target; outside-click handler covers it
                if (popoverRef.current?.contains(next) || triggerRef.current?.contains(next))
                  return;
                dismissTeamPicker();
              }}
              style={{
                position: 'fixed',
                top: popoverAnchor.top,
                left: popoverAnchor.left,
                background: '#fff',
                color: 'var(--fg1, #333f48)',
                border: '1px solid var(--border, #d6d9dc)',
                borderRadius: 8,
                padding: 6,
                width: PICKER_WIDTH,
                zIndex: 9999,
                boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
              }}
            >
              <div
                style={{
                  fontSize: 'var(--fs-12)',
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--fg3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '4px 8px 6px',
                }}
              >
                Teams on this project
              </div>
              {project.teams.length === 0 ? (
                <p
                  style={{
                    padding: '4px 8px 8px',
                    margin: 0,
                    fontSize: 'var(--fs-12)',
                    color: 'var(--fg3)',
                    lineHeight: 1.5,
                  }}
                >
                  No teams on this project yet. Add teams from the <strong>Members</strong> tab.
                </p>
              ) : (
                project.teams.map((t) => {
                  const isAssigned = assigned.has(t.teamId);
                  return (
                    <button
                      key={t.teamId}
                      type="button"
                      aria-pressed={isAssigned}
                      onClick={() => handleTaskTeamToggle(task, t, isAssigned)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '7px 8px',
                        border: 'none',
                        borderRadius: 6,
                        background: isAssigned ? 'var(--ut-bluebonnet-wash)' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 'var(--fs-14)',
                        color: 'var(--fg1, #333f48)',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: t.color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t.name}
                      </span>
                      <span style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)' }}>
                        {t.memberCount}
                      </span>
                      <span
                        aria-hidden="true"
                        style={{ width: 14, flexShrink: 0, color: 'var(--ut-bluebonnet)' }}
                      >
                        {isAssigned && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M2.5 7.5l3 3 6-6.5" />
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>,
            document.body,
          );
        })()}
    </>
  );
}
